import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import commonEn from "@/locales/en-GB/common.json" with { type: "json" };
import devicesEn from "@/locales/en-GB/devices.json" with { type: "json" };
import loansEn from "@/locales/en-GB/loans.json" with { type: "json" };
import marketingEn from "@/locales/en-GB/marketing.json" with { type: "json" };
import navEn from "@/locales/en-GB/nav.json" with { type: "json" };
import pagesEn from "@/locales/en-GB/pages.json" with { type: "json" };
import servicesEn from "@/locales/en-GB/services.json" with { type: "json" };
import commonEs from "@/locales/es-AR/common.json" with { type: "json" };
import devicesEs from "@/locales/es-AR/devices.json" with { type: "json" };
import loansEs from "@/locales/es-AR/loans.json" with { type: "json" };
import marketingEs from "@/locales/es-AR/marketing.json" with { type: "json" };
import navEs from "@/locales/es-AR/nav.json" with { type: "json" };
import pagesEs from "@/locales/es-AR/pages.json" with { type: "json" };
import servicesEs from "@/locales/es-AR/services.json" with { type: "json" };

import {
  DEFAULT_LOCALE,
  type AppLocale,
  localeForPath,
  readStoredLocaleChoice,
  writeStoredLocale,
} from "./locales";

export const I18N_NAMESPACES = [
  "common",
  "nav",
  "loans",
  "services",
  "devices",
  "pages",
  "marketing",
] as const;

void i18n.use(initReactI18next).init({
  resources: {
    "en-GB": {
      common: commonEn,
      nav: navEn,
      loans: loansEn,
      services: servicesEn,
      devices: devicesEn,
      pages: pagesEn,
      marketing: marketingEn,
    },
    "es-AR": {
      common: commonEs,
      nav: navEs,
      loans: loansEs,
      services: servicesEs,
      devices: devicesEs,
      pages: pagesEs,
      marketing: marketingEs,
    },
  },
  lng: localeForPath(
    typeof window === "undefined" ? "/" : window.location.pathname,
    readStoredLocaleChoice(),
  ),
  fallbackLng: DEFAULT_LOCALE,
  defaultNS: "common",
  ns: [...I18N_NAMESPACES],
  interpolation: { escapeValue: false },
  returnNull: false,
});

export async function setAppLocale(locale: AppLocale): Promise<void> {
  writeStoredLocale(locale);
  await applyVisitLocale(locale);
}

/**
 * Switches language for this visit without writing localStorage.
 * Marketing uses it so a first look at the homepage does not flip the console.
 */
export async function applyVisitLocale(locale: AppLocale): Promise<void> {
  document.documentElement.lang = locale;

  if (getAppLocale() === locale) {
    return;
  }

  await i18n.changeLanguage(locale);
  // Lazy import avoids a cycle: format.ts imports getAppLocale from here.
  const { resetFormatters } = await import("@/lib/format");
  resetFormatters();
}

export function getAppLocale(): AppLocale {
  const current = i18n.language;

  return current === "es-AR" ? "es-AR" : "en-GB";
}

/** Call once at startup so the document language matches the stored preference. */
export function applyDocumentLocale(): void {
  document.documentElement.lang = getAppLocale();
}

export type { AppLocale } from "./locales";
export { i18n };
export default i18n;
